import React, { Fragment } from 'react';

import { connect } from 'react-redux';

import clsx from 'clsx';
import _get from 'lodash/get';

import {
    Section,
    SeeMore,
} from 'reaxl';
import { useFeatures } from 'reaxl-features';
import { KBBDealerRatings } from 'reaxl-ratings-reviews';

import {
    vdpActiveInteractionDuck,
    vdpResultsDuck,
} from '@/ducks/vdp';

import DealerTitle from '@/components/vehicledetails/DealerTitle';

import DealerDisclosureContainer from './DealerDisclosureContainer';

const renderSellerComments = (vehicleDescription, sellerComments) => {
    const content = (
        <>
            {vehicleDescription
                && (
                    <p
                        className="text-left"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: vehicleDescription }}
                    />
                )}
            {sellerComments
                && (
                    <p
                        className="text-left"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: sellerComments }}
                    />
                )}
            <DealerDisclosureContainer />
        </>
    );
    return (
        <SeeMore>
            {content}
        </SeeMore>
    );
};

function SellerCommentsHeading({ children }) {
    return (
        <Fragment key="sellerCommentsHeading">
            More from the seller
            {children}
        </Fragment>
    );
}

function StockNumberAndVIN({ stockNumber, vin, isRestyled }) {
    const hasStockNum = stockNumber && !stockNumber.startsWith('Info');

    return (hasStockNum || vin) ? (
        <div className={clsx(
            'text-gray-dark text-normal',
            {
                'text-size-300 text-size-sm-400 margin-bottom-2': isRestyled,
                'text-size-300': !isRestyled,
            }
        )}
        >
            {hasStockNum && (
                <span className="display-inline-block">
                    Stock#:
                    {'\u00A0'}
                    {stockNumber}
                </span>
            )}
            {hasStockNum && vin && (
                <span className="text-gray-dark padding-horizontal-4 display-none display-sm-inline-block">
                    |
                </span>
            )}
            {vin && (
                <span className="display-block display-sm-inline-block">
                    VIN:
                    {'\u00A0'}
                    {vin}
                </span>
            )}
        </div>
    ) : null;
}

function DealerDetailAndRating({ listing, defaultShowModal, isDealerRatingDisabled }) {
    const { owner = {}, dealerConfig, id: listingId } = listing;
    const { website, name, rating, id: ownerId } = owner;
    const isLink = website?.href && !(!!dealerConfig && dealerConfig?.displayOwnerSnapshotSiteLink === false);

    return (
        <div className="padding-bottom-3 text-size-500">
            <DealerTitle
                isLink={isLink}
                href={website?.href}
                analyticsData={{
                    inventoryId: listingId,
                    ownerId,
                    placement: 'snapshot',
                }}
                componentClass="h2"
            >
                {name}
            </DealerTitle>
            {!isDealerRatingDisabled && !!rating?.value && (
                <div className="text-normal">
                    <KBBDealerRatings
                        className="margin-top-2"
                        owner={owner}
                        widgetType="inline"
                        showCount
                        enableImpression
                        defaultShowModal={defaultShowModal}
                    />
                </div>
            )}
        </div>
    );
}

export function AdditionalVehicleDetailsContainer({
    sellerComments,
    vehicleDescription,
    stockNumber,
    showSellerComments,
    vin,
    listing,
    showKBBDealerModal,
}) {
    const {
        dealer_name_relocation: [isDealerNameRelocationFlagEnabled],
        disable_dealer_rating: [isDealerRatingDisabled],
    } = useFeatures([
        'dealer_name_relocation',
        'disable_dealer_rating',
    ]);

    const isPrivateSeller = _get(listing, 'owner.privateSeller', false);
    const isDealerNameRelocation = isDealerNameRelocationFlagEnabled && !isPrivateSeller;

    if (!showSellerComments && vin) {
        showSellerComments = true;
    }

    if (!showSellerComments) {
        return null;
    }

    const sellerCommentsContent = (sellerComments || vehicleDescription)
        ? renderSellerComments(vehicleDescription, sellerComments) : <DealerDisclosureContainer />;

    const stockNumberAndVinComponent = (
        <StockNumberAndVIN
            stockNumber={stockNumber}
            vin={vin}
            isRestyled={isDealerNameRelocation}
        />
    );

    return (
        <Section
            heading={(
                <SellerCommentsHeading>
                    {!isDealerNameRelocation && stockNumberAndVinComponent}
                </SellerCommentsHeading>
            )}
            alignment="left"
            uiContext="level-3"
            id="sellerComments"
            className="padding-vertical-4 padding-vertical-md-5"
        >
            {isDealerNameRelocation && (
                <>
                    <DealerDetailAndRating
                        listing={listing}
                        showKBBDealerModal={showKBBDealerModal}
                        isDealerRatingDisabled={isDealerRatingDisabled}
                    />
                    {stockNumberAndVinComponent}
                </>
            )}
            {sellerCommentsContent}
        </Section>
    );
}

const mapStateToProps = (state) => {
    const listing = vdpResultsDuck.selectors.getVdpActiveInventory(state);
    const showKBBDealerModal = vdpActiveInteractionDuck.selectors.getShowKBBDealerModal(state);
    const stockNumber = listing.stockNumber || '';
    const { owner = {}, vin } = listing;
    const { disclaimer } = owner;
    const vehicleDescription = listing.fullDescription;
    const { sellerComments } = owner;
    const vehicleFeatures = listing.features;
    return {
        sellerComments,
        vehicleFeatures,
        vehicleDescription,
        stockNumber,
        showSellerComments: stockNumber || sellerComments || vehicleDescription || disclaimer,
        vin,
        listing,
        showKBBDealerModal,
    };
};

export default connect(mapStateToProps)(React.memo(AdditionalVehicleDetailsContainer));
